import React, { useState, useMemo, useEffect, useRef, useCallback, ReactElement } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, Polyline, useMap } from 'react-leaflet';
import FullscreenControl from '@/components/map/FullscreenControl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import type { Voter, Route, EngagementTier } from '../types';
import { VoterVotingRecord } from '@/components/VoterVotingRecord';
import { tierColor } from '@/lib/mapColors';

interface InteractiveMapProps {
  voters?: Voter[];
  selectedRoute?: Route | null;
  onVoterClick?: (voter: Voter) => void;
  geojsonBoundaries?: GeoJSON.FeatureCollection[] | null;
  center?: [number, number];
  zoom?: number;
}

/**
 * Marker for one voter, colored by engagement tier.
 *
 * Colors come from `@/lib/mapColors` — i.e. from glossary.ts's TIER_PALETTE,
 * the same table the app's legend and Glossary page render. The hand-rolled
 * ladder this replaces had two defects worth naming:
 *
 *   1. Its `anchor` branch was UNREACHABLE. The `hot` branch above it tested
 *      the identical `score >= 60 && score < 75` band, so every anchor that
 *      reached the ladder by score came out red.
 *   2. That band was wrong anyway. `classify_tier` puts 75+ in the top tier;
 *      `anchor` and `super_voter` are two names for that one 75–100 band, not
 *      two tiers, so the ladder also produced two different "top" colors.
 *
 * Net effect: this map painted anchors red or gold while VoterMapPanel painted
 * them red or emerald, and the Glossary said yellow.
 */
const createMarkerIcon = (tier: EngagementTier | undefined, score: number): L.DivIcon => {
  const color = tierColor(tier, score);

  return L.divIcon({
    className: 'custom-voter-marker',
    html: `
      <div style="
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 10px;
        font-weight: bold;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        ${Math.round(score)}
      </div>
    `,
    iconSize: [24, 24] as [number, number],
    iconAnchor: [12, 12] as [number, number],
    popupAnchor: [0, -12] as [number, number],
  });
};

/**
 * FitToData — frames the map to the actual points it's showing (route stops,
 * voter pins, route line) instead of leaving it parked at the county-wide
 * default center/zoom, which stranded a small turf in a sea of empty map.
 * Re-fits whenever the set of points changes (a different route loads) and
 * whenever the container resizes (e.g. the popup enters/exits full screen),
 * so the frame stays tight at any size.
 */
function FitToData({ points }: { points: [number, number][] }): null {
  const map = useMap();
  // Keep the latest points reachable from the (stable) resize handler without
  // re-subscribing on every render.
  const pointsRef = useRef(points);
  pointsRef.current = points;

  const fit = useCallback(() => {
    const pts = pointsRef.current;
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], Math.max(map.getZoom(), 16));
      return;
    }
    const bounds = L.latLngBounds(pts.map((p) => L.latLng(p[0], p[1])));
    if (!bounds.isValid()) return;
    // padding keeps pins off the edges; maxZoom stops a tight cluster from
    // zooming in past street level.
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
  }, [map]);

  // Cheap change signature: count + first + last coord. Enough to detect a
  // route swap without re-fitting on every unrelated re-render.
  const signature = points.length
    ? `${points.length}:${points[0].join(',')}:${points[points.length - 1].join(',')}`
    : '';
  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Leaflet fires 'resize' after invalidateSize() — which FullscreenControl
  // calls on fullscreenchange — so a full-screen toggle (or any container
  // resize) re-tightens the frame instead of leaving extra empty space.
  useEffect(() => {
    map.on('resize', fit);
    return () => {
      map.off('resize', fit);
    };
  }, [map, fit]);

  return null;
}

export const InteractiveMap: React.FC<InteractiveMapProps> = ({
  voters = [],
  selectedRoute = null,
  onVoterClick,
  geojsonBoundaries = null,
  center = [39.21, -94.63], // Platte County, MO
  zoom = 10,
}): ReactElement => {
  const [selectedVoter, setSelectedVoter] = useState<Voter | null>(null);
  const [mapKey, setMapKey] = useState<number>(0);
  // Tier 0 display toggle: hide the connecting line to show targets only.
  const [showRouteLine, setShowRouteLine] = useState<boolean>(true);

  // The walking route line. Prefer the OSM router's street-following geometry
  // (Tier 2); otherwise draw straight segments between stops in sequence order
  // (Tier 1) — dashed, to signal it's approximate.
  const { routeLine, streetFollowing } = useMemo<{
    routeLine: [number, number][];
    streetFollowing: boolean;
  }>(() => {
    if (!selectedRoute) return { routeLine: [], streetFollowing: false };

    if (selectedRoute.route_geometry) {
      try {
        const parsed = JSON.parse(selectedRoute.route_geometry);
        if (Array.isArray(parsed) && parsed.length > 1) {
          return { routeLine: parsed as [number, number][], streetFollowing: true };
        }
      } catch {
        // fall through to straight segments
      }
    }

    const stops = (selectedRoute.stops ?? [])
      .filter((s) => s.voter?.lat != null && s.voter?.lng != null)
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((s) => [s.voter.lat as number, s.voter.lng as number] as [number, number]);
    return { routeLine: stops, streetFollowing: false };
  }, [selectedRoute]);

  const hasRouteLine = routeLine.length > 1;

  // Every coordinate the map is actually drawing — used to auto-frame it.
  const fitPoints = useMemo<[number, number][]>(() => {
    const pts: [number, number][] = [];
    voters.forEach((v) => {
      if (v.lat != null && v.lng != null) pts.push([v.lat, v.lng]);
    });
    (selectedRoute?.stops ?? []).forEach((s) => {
      if (s.voter?.lat != null && s.voter?.lng != null)
        pts.push([s.voter.lat, s.voter.lng]);
    });
    routeLine.forEach((p) => pts.push(p));
    return pts;
  }, [voters, selectedRoute, routeLine]);

  const handleVoterClick = (voter: Voter): void => {
    setSelectedVoter(voter);
    if (onVoterClick) {
      onVoterClick(voter);
    }
  };

  // Voter pins
  const voterMarkers = voters
    .filter((v) => v.lat && v.lng)
    .map((voter) => (
      <Marker
        key={`voter-${voter.id}`}
        position={[voter.lat as number, voter.lng as number]}
        icon={createMarkerIcon(voter.engagement_tier, voter.score)}
        eventHandlers={{
          click: () => handleVoterClick(voter),
        }}
      >
        <Popup>
          <div className="text-sm w-64">
            <h3 className="font-bold text-slate-900">
              {voter.first_name} {voter.last_name}
            </h3>
            <p className="text-xs text-slate-600">{voter.address}</p>
            <p className="text-xs text-slate-600">
              {voter.city}, {voter.zip_code}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Score:</span>
              <span
                className="px-2 py-1 rounded text-white text-xs font-bold"
                style={{
                  backgroundColor:
                    voter.score >= 75
                      ? '#fbbf24'
                      : voter.score >= 60
                      ? '#ef4444'
                      : voter.score >= 40
                      ? '#f97316'
                      : '#3b82f6',
                }}
              >
                {voter.score}
              </span>
            </div>
            <div className="mt-1 text-xs">
              <p>
                <span className="font-semibold">Tier:</span>{' '}
                {voter.engagement_tier || 'unclassified'}
              </p>
              <p>
                <span className="font-semibold">Party:</span> {voter.party || 'N/A'}
              </p>
              <p>
                <span className="font-semibold">Elections:</span> {voter.election_count}
              </p>
            </div>
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Voting record
              </p>
              <VoterVotingRecord voterId={voter.id} />
            </div>
          </div>
        </Popup>
      </Marker>
    ));

  // Route stops overlay
  const routeStopMarkers =
    selectedRoute && selectedRoute.stops
      ? selectedRoute.stops.map((stop) => {
          const voter = stop.voter;
          if (!voter.lat || !voter.lng) return null;
          return (
            <Marker
              key={`route-stop-${stop.id}`}
              position={[voter.lat as number, voter.lng as number]}
              icon={L.divIcon({
                className: 'route-stop-marker',
                html: `
                  <div style="
                    background-color: #dc2626;
                    border: 3px solid white;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 12px;
                    font-weight: bold;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                  ">
                    ${stop.sequence}
                  </div>
                `,
                iconSize: [32, 32] as [number, number],
                iconAnchor: [16, 16] as [number, number],
                popupAnchor: [0, -16] as [number, number],
              })}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-bold">Stop #{stop.sequence}</p>
                  <p>
                    {voter.first_name} {voter.last_name}
                  </p>
                  <p className="text-xs text-slate-600">{voter.address}</p>
                  <p className="text-xs text-red-600 font-semibold mt-1">
                    Status: {stop.status}
                  </p>
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                      Voting record
                    </p>
                    <VoterVotingRecord voterId={voter.id} />
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })
      : [];

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden shadow-lg border border-border-base">
      {hasRouteLine && (
        <button
          type="button"
          onClick={() => setShowRouteLine((v) => !v)}
          className="
            absolute top-2 right-2 z-[1000] px-3 py-1.5 rounded-md text-xs font-bold
            bg-bg-raised/95 border border-border-base text-fg-primary shadow
            hover:bg-bg-elevated transition-colors
          "
          title={
            showRouteLine
              ? 'Hide the connecting line and show targets only'
              : 'Show the walking route line'
          }
        >
          {showRouteLine ? 'Targets only' : 'Show route line'}
        </button>
      )}
      <MapContainer
        key={mapKey}
        center={center}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        className="z-10"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap contributors'
          maxZoom={19}
        />

        <FullscreenControl />

        <FitToData points={fitPoints} />

        {/* GeoJSON precinct boundaries */}
        {geojsonBoundaries &&
          geojsonBoundaries.map((boundary, idx) => (
            <GeoJSON key={`boundary-${idx}`} data={boundary} />
          ))}

        {/* Clustered voter markers */}
        <MarkerClusterGroup maxClusterRadius={80}>
          {voterMarkers}
        </MarkerClusterGroup>

        {/* Walking route line: OSM street-following (solid) or straight
            fallback (dashed). Hidden in targets-only mode. */}
        {showRouteLine && hasRouteLine && (
          <Polyline
            positions={routeLine}
            pathOptions={{
              color: '#dc2626',
              weight: 4,
              opacity: 0.85,
              dashArray: streetFollowing ? undefined : '6 8',
            }}
          />
        )}

        {/* Route stop markers (drawn on top) */}
        {routeStopMarkers}
      </MapContainer>
    </div>
  );
};

export default InteractiveMap;
